/**
 * Dialog for creating a native SQL Server backup (.bak file).
 * Shows server-side file path input, backup type, compression options,
 * and progress indicator during backup.
 * In Personal mode, backups are limited to databases <= 1 GB.
 */

import { useState, useEffect, useRef } from 'react';
import { Database, AlertTriangle, HardDrive } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { ProgressIndicator } from './ProgressIndicator';
import { PersonalModeBackupRestoreLimitMessage } from './PersonalModeBackupRestoreLimitMessage';
import { useBackupBak } from '../hooks/useBackupBak';
import { useOperationStatus } from '../hooks/useTaskProgress';
import { useBackupRestoreStore } from '../store/backupRestoreStore';
import { useProFeature } from '../../../core/licensing/hooks/useProFeature';
import { UpgradeDialog } from '../../../core/licensing';
import { checkPersonalModeLimits, fetchDatabaseSize, type PersonalModeLimitCheck } from '../utils/personalModeLimits';
import { getServerDirectoryFromFilePath } from '../utils/serverPathUtils';
import { toast } from '../../../components/ui/toaster';
import { mssqlCancelBak, mssqlGetDefaultBackupDirectory } from '../services/backupRestoreApi';
import { ServerPathPickerDialog } from './ServerPathPickerDialog';
import type { MssqlBackupType } from '../types/backupRestore';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type BackupDatabaseDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  databaseName: string;
};

const BACKUP_TYPES: { value: MssqlBackupType; label: string; description: string }[] = [
  { value: 'full', label: 'Full', description: 'Complete backup of the entire database' },
  { value: 'differential', label: 'Differential', description: 'Backup changes since last full backup' },
  { value: 'log', label: 'Transaction Log', description: 'Backup of the transaction log only' },
];

function buildBackupFileName(databaseName: string): string {
  return `${databaseName}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.bak`;
}

function joinServerPath(directory: string, fileName: string): string {
  const trimmedDir = directory.trim();
  if (!trimmedDir) {
    return fileName;
  }

  const separator = trimmedDir.includes('\\') ? '\\' : '/';
  const normalizedDir = trimmedDir.endsWith('\\') || trimmedDir.endsWith('/') ? trimmedDir : `${trimmedDir}${separator}`;
  return `${normalizedDir}${fileName}`;
}

export function BackupDatabaseDialog({
  open,
  onOpenChange,
  connectionId,
  databaseName,
}: BackupDatabaseDialogProps) {
  const [serverFilePath, setServerFilePath] = useState(
    `C:\\Backups\\${buildBackupFileName(databaseName)}`
  );
  const [backupType, setBackupType] = useState<MssqlBackupType>('full');
  const [compression, setCompression] = useState(true);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [isCheckingSize, setIsCheckingSize] = useState(false);
  const [limitCheck, setLimitCheck] = useState<PersonalModeLimitCheck | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [serverDefaultBackupDirectory, setServerDefaultBackupDirectory] = useState<string | null>(null);
  const [isDetectingBackupDirectory, setIsDetectingBackupDirectory] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showServerFolderPicker, setShowServerFolderPicker] = useState(false);
  const hasTouchedServerFilePathRef = useRef(false);

  const operation = useOperationStatus(operationId);
  const { removeOperation, updateOperation } = useBackupRestoreStore();
  const { canUse: canUseFeature } = useProFeature('backup-restore-full');

  const { backupDatabase, isBackingUp, reset } = useBackupBak({
    onOperationCreated: (opId) => setOperationId(opId),
    onSuccess: (result, opId) => {
      setOperationId(opId);

      if (result.success) {
        toast.success({
          title: 'Backup Complete',
          description: `Database "${databaseName}" backed up successfully`,
        });
      } else if (result.cancelled) {
        toast.success({
          title: 'Backup Cancelled',
          description: `Backup of "${databaseName}" was cancelled`,
        });
      } else {
        toast.error({
          title: 'Backup Failed',
          description: result.error || 'Unknown error occurred',
        });
      }
    },
    onError: (error) => {
      toast.error({
        title: 'Backup Failed',
        description: error.message,
      });
    },
  });

  // Check database size when dialog opens (Personal mode only)
  useEffect(() => {
    if (open && !canUseFeature && !limitCheck && !isCheckingSize) {
      setIsCheckingSize(true);
      fetchDatabaseSize(connectionId, databaseName, 'mssql')
        .then((result) => {
          const check = checkPersonalModeLimits(result.success ? result.sizeInBytes ?? null : null);
          setLimitCheck(check);
        })
        .finally(() => {
          setIsCheckingSize(false);
        });
    }
  }, [open, canUseFeature, connectionId, databaseName, limitCheck, isCheckingSize]);

  // Detect SQL Server default backup directory (best-effort, server-side)
  useEffect(() => {
    if (!open || isDetectingBackupDirectory || serverDefaultBackupDirectory) {
      return;
    }

    setIsDetectingBackupDirectory(true);
    mssqlGetDefaultBackupDirectory(connectionId)
      .then((result) => {
        if (!result.success || !result.directory?.trim()) {
          return;
        }

        const directory = result.directory.trim();
        setServerDefaultBackupDirectory(directory);

        // Only auto-apply if the user hasn't changed the path yet
        if (!hasTouchedServerFilePathRef.current) {
          setServerFilePath(joinServerPath(directory, buildBackupFileName(databaseName)));
        }
      })
      .finally(() => {
        setIsDetectingBackupDirectory(false);
      });
  }, [open, isDetectingBackupDirectory, serverDefaultBackupDirectory, connectionId, databaseName]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setLimitCheck(null);
      setServerDefaultBackupDirectory(null);
      setIsDetectingBackupDirectory(false);
      hasTouchedServerFilePathRef.current = false;
      setShowServerFolderPicker(false);
    }
  }, [open]);

  const handleUseServerDefault = () => {
    if (!serverDefaultBackupDirectory?.trim()) {
      toast.error({
        title: 'Default Backup Folder Unavailable',
        description: 'Could not detect the SQL Server default backup directory. Please enter a server path manually.',
      });
      return;
    }

    hasTouchedServerFilePathRef.current = true;
    setServerFilePath(joinServerPath(serverDefaultBackupDirectory, buildBackupFileName(databaseName)));
  };

  const handleStartBackup = () => {
    if (!serverFilePath.trim()) {
      toast.error({
        title: 'Invalid Path',
        description: 'Please enter a file path on the SQL Server',
      });
      return;
    }

    backupDatabase({
      connectionId,
      databaseName,
      serverFilePath: serverFilePath.trim(),
      backupType,
      compression,
      ...(limitCheck?.sizeInBytes !== null && limitCheck?.sizeInBytes !== undefined
        ? { preflight: { databaseSizeBytes: limitCheck.sizeInBytes } }
        : {}),
    });
  };

  const handleCancelBackup = async () => {
    if (!operationId || isCancelling) {
      return;
    }

    setIsCancelling(true);
    updateOperation(operationId, { message: 'Cancelling...' });

    try {
      const result = await mssqlCancelBak(operationId);
      if (!result.success) {
        toast.error({
          title: 'Cancel Failed',
          description: result.error || 'Unable to cancel the backup',
        });
        updateOperation(operationId, { message: result.error });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unable to cancel the backup';
      toast.error({
        title: 'Cancel Failed',
        description: errorMessage,
      });
      updateOperation(operationId, { message: errorMessage });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleClose = () => {
    if (operationId) {
      removeOperation(operationId);
    }
    setOperationId(null);
    setLimitCheck(null);
    reset();
    onOpenChange(false);
  };

  const isComplete =
    operation?.status === 'succeeded' ||
    operation?.status === 'failed' ||
    operation?.status === 'cancelled';
  const canClose = isComplete || (!isBackingUp && !operationId);
  const hasStarted = isBackingUp || operationId !== null;
  const showingLimitMessage = limitCheck?.exceedsLimit && !canUseFeature;

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => !isOpen && canClose && handleClose()}>
        <DialogContent data-testid="backup-database-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
                <Database className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <DialogTitle>Backup Database (.bak)</DialogTitle>
                <DialogDescription className="mt-1">
                  Create a native SQL Server backup of &quot;{databaseName}&quot;
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {isCheckingSize ? (
              <div className="flex flex-col items-center justify-center py-8 gap-3">
                <RandomDotmSpinner size={32} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Checking database size...</p>
              </div>
            ) : showingLimitMessage && limitCheck ? (
              <PersonalModeBackupRestoreLimitMessage
                limitCheck={limitCheck}
                operationType="backup"
                onUpgrade={() => setShowUpgradeDialog(true)}
                onCancel={handleClose}
              />
            ) : (
              <>
                {/* Server-side warning */}
                <div className="flex items-start gap-3 p-3 rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950 dark:border-amber-800">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                  <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                    <HardDrive className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                      The backup file will be created <strong>on the SQL Server machine</strong>, not
                      your local computer. Ensure the path exists and the SQL Server service account has
                      write permissions.
                    </span>
                  </div>
                </div>

                {!hasStarted ? (
                  <>
                    {/* Server file path */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <Label htmlFor="server-file-path">File path on SQL Server</Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleUseServerDefault}
                            disabled={isDetectingBackupDirectory}
                            data-testid="backup-use-server-default"
                          >
                            {isDetectingBackupDirectory ? (
                              <>
                                <RandomDotmSpinner size={12} />
                                Detecting...
                              </>
                            ) : (
                              'Use server default'
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowServerFolderPicker(true)}
                            data-testid="backup-browse-server-folder"
                          >
                            Browse...
                          </Button>
                        </div>
                      </div>
                      <Input
                        id="server-file-path"
                        value={serverFilePath}
                        onChange={(e) => {
                          hasTouchedServerFilePathRef.current = true;
                          setServerFilePath(e.target.value);
                        }}
                        placeholder="C:\\Backups\\database.bak"
                        data-testid="backup-server-path-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Example: C:\SQLBackups\{databaseName}.bak or /var/opt/mssql/backups/{databaseName}.bak
                      </p>
                      {serverDefaultBackupDirectory && (
                        <p className="text-xs text-muted-foreground">
                          Server default backup folder: <span className="font-mono">{serverDefaultBackupDirectory}</span>
                        </p>
                      )}
                    </div>

                    {/* Backup type */}
                    <div className="space-y-3">
                      <Label htmlFor="backup-type">Backup type</Label>
                      <Select value={backupType} onValueChange={(v) => setBackupType(v as MssqlBackupType)}>
                        <SelectTrigger id="backup-type" data-testid="backup-type-select" className="h-auto py-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="p-2">
                          {BACKUP_TYPES.map((type) => (
                            <SelectItem key={type.value} value={type.value} className="py-2">
                              <div className="flex flex-col items-start text-left">
                                <span>{type.label}</span>
                                <span className="text-xs text-muted-foreground">{type.description}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Compression option */}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="compression"
                        checked={compression}
                        onCheckedChange={(checked) => setCompression(checked === true)}
                        data-testid="backup-compression-checkbox"
                      />
                      <Label htmlFor="compression" className="text-sm font-normal cursor-pointer">
                        Use backup compression (recommended, requires SQL Server 2008+)
                      </Label>
                    </div>
                  </>
                ) : (
                  /* Progress indicator */
                  <ProgressIndicator
                    status={operation?.status || 'inProgress'}
                    progress={operation?.progress}
                    message={operation?.message}
                    error={operation?.error}
                    data-testid="backup-progress"
                  />
                )}
              </>
            )}
          </div>

          {!showingLimitMessage && !isCheckingSize && (
            <DialogFooter>
              {!hasStarted ? (
                <>
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    data-testid="backup-dialog-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleStartBackup}
                    disabled={!serverFilePath.trim()}
                    data-testid="backup-dialog-start"
                  >
                    Start Backup
                  </Button>
                </>
              ) : (
                <Button
                  variant={isComplete ? 'default' : 'outline'}
                  onClick={isComplete ? handleClose : handleCancelBackup}
                  disabled={isComplete ? !canClose : isCancelling}
                  data-testid="backup-dialog-close"
                >
                  {isComplete ? 'Done' : isCancelling ? 'Cancelling...' : 'Cancel'}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
      />

      <ServerPathPickerDialog
        open={showServerFolderPicker}
        onOpenChange={setShowServerFolderPicker}
        connectionId={connectionId}
        mode="directory"
        initialDirectoryPath={
          serverDefaultBackupDirectory?.trim() ||
          getServerDirectoryFromFilePath(serverFilePath) ||
          'C:\\Backups'
        }
        title="Select Backup Folder (SQL Server)"
        description="Select a folder on the SQL Server machine for the .bak file."
        onSelectPath={(folderPath) => {
          hasTouchedServerFilePathRef.current = true;
          setServerFilePath(joinServerPath(folderPath, buildBackupFileName(databaseName)));
        }}
      />
    </>
  );
}
